import { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

export const ApexChart = ({ installmentData = [] }) => {
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(11); // Default for desktop

    // Update items per page based on screen size
    useEffect(() => {
        const handleResize = () => {
            setItemsPerPage(window.innerWidth < 768 ? 6 : 11);
        };
        
        handleResize(); // Set initial value
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [state, setState] = useState({
        series: [
            {
                name: "Paid",
                type: "column",
                data: [],
            },
            {
                name: "Upcoming",
                type: "column",
                data: [],
            },
            // {
            //     name: "Revenue",
            //     type: "line",
            //     data: [],
            // },
        ],
        options: {
            chart: {
                type: "bar",
                height: 350,
                stacked: false,
                toolbar: { show: false },
                zoom: { enabled: false },
            },
            plotOptions: {
                bar: {
                    columnWidth: "80%",
                    endingShape: "rounded",
                    distributed: false,
                },
            },
            colors: ["#006E7E", "rgba(0, 110, 126, 0.2)"], // Paid (dark teal), Upcoming (light teal)
            dataLabels: { enabled: false },
            stroke: {
                width: [0, 0],
                curve: "smooth",
            },
            fill: {
                opacity: [1, 0.2],
                colors: ["#006E7E", "rgba(0, 110, 126, 0.2)"],
            },
            xaxis: {
                categories: [],
            },
            yaxis: {
                labels: {
                    formatter: (val) => {
                        if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
                        return val.toFixed(0);
                    },
                    style: {
                        colors: "#4B4B4B",
                        fontSize: "14px",
                    },
                },
                min: 0,
            },
            tooltip: {
                enabled: true,
                shared: true,
                intersect: false,
            },
            legend: {
                show: true,
                position: "top",
                horizontalAlign:"right",
                markers: {
                    width: 12,
                    height: 12,
                    radius: 4,
                },
                itemMargin: {
                    horizontal: 10,
                    vertical: 0,
                },
                labels: {
                    useSeriesColors: false,
                    colors: "#000000",
                },
            },
            grid: {
                borderColor: "#EDEDED",
                strokeDashArray: 4,
            },
        },
    });

    useEffect(() => {
        if (installmentData && installmentData.length > 0) {
            // Group installments by quarter (3 months)
            const quarterlyData = {};
            const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            
            installmentData.forEach(item => {
                if (item.rawDate) {
                    const month = item.rawDate.getMonth();
                    const year = item.rawDate.getFullYear();
                    const quarter = Math.floor(month / 3); // 0-3 (Q1-Q4)
                    const quarterStartMonth = quarter * 3;
                    const quarterKey = `${monthNames[quarterStartMonth]}-${monthNames[Math.min(quarterStartMonth + 2, 11)]} ${year}`;
                    
                    if (!quarterlyData[quarterKey]) {
                        quarterlyData[quarterKey] = {
                            paid: 0,
                            upcoming: 0,
                            total: 0,
                            quarter: quarter,
                            year: year,
                            sortKey: year * 10 + quarter,
                        };
                    }
                    
                    const amount = parseFloat(item.installmentAmount) || parseFloat(item.amount) || 0;
                    quarterlyData[quarterKey].total += amount;
                    
                    if (item.status === 'Paid') {
                        quarterlyData[quarterKey].paid += amount;
                    } else {
                        quarterlyData[quarterKey].upcoming += amount;
                    }
                }
            });

            // Sort by date and prepare chart data
            const sortedQuarters = Object.entries(quarterlyData)
                .sort(([, a], [, b]) => a.sortKey - b.sortKey)
                .map(([key]) => key);

            // Calculate total pages
            const pages = Math.ceil(sortedQuarters.length / itemsPerPage);
            setTotalPages(pages);

            // Get current page data
            const startIndex = currentPage * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            const limitedQuarters = sortedQuarters.slice(startIndex, endIndex);

            const paidData = limitedQuarters.map(quarter => {
                return quarterlyData[quarter].paid;
            });
            
            const upcomingData = limitedQuarters.map(quarter => {
                return quarterlyData[quarter].upcoming;
            });
            
            const totalData = limitedQuarters.map(quarter => {
                return quarterlyData[quarter].total;
            });

            console.log('Chart Data:', { paidData, upcomingData, totalData, sortedQuarters: limitedQuarters });

            setState(prevState => ({
                series: [
                    {
                        name: "Paid",
                        type: "column",
                        data: paidData,
                    },
                    {
                        name: "Upcoming",
                        type: "column",
                        data: upcomingData,
                    },
                ],
                options: {
                    ...prevState.options,
                    xaxis: {
                        ...prevState.options.xaxis,
                        categories: limitedQuarters,
                        labels: {
                            rotate: -45,
                            rotateAlways: false,
                            hideOverlappingLabels: true,
                            style: {
                                fontSize: '12px',
                            },
                        },
                    },
                },
            }));
        }
    }, [installmentData, currentPage, itemsPerPage]);

    const handlePrevious = () => {
        if (currentPage > 0) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNext = () => {
        if (currentPage < totalPages - 1) {
            setCurrentPage(currentPage + 1);
        }
    };

    return (
        <div>
            <ReactApexChart
                options={state.options}
                series={state.series}
                type="bar"
                height={350}
            />
            {/* Uncomment below for pagination navigation */}
            {/* {totalPages > 1 && (
                <div className="flex justify-between items-center px-4 py-3 border-t border-gray-200">
                    <button
                        onClick={handlePrevious}
                        disabled={currentPage === 0}
                        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                        aria-label="Previous page"
                    >
                        <LeftOutlined className="text-xs" />
                        <span className="hidden sm:inline">Previous</span>
                    </button>
                    <span className="text-sm font-medium text-gray-600">
                        {currentPage + 1} / {totalPages}
                    </span>
                    <button
                        onClick={handleNext}
                        disabled={currentPage === totalPages - 1}
                        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                        aria-label="Next page"
                    >
                        <span className="hidden sm:inline">Next</span>
                        <RightOutlined className="text-xs" />
                    </button>
                </div>
            )} */}
        </div>
    );
};
